export const SUITS = ['clubs', 'diamonds', 'hearts', 'spades'] as const
export const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'jack', 'queen', 'king', 'ace'] as const

export const POINTS_PER_BONUS_CARD = 20

export type Suit = (typeof SUITS)[number]
export type Rank = (typeof RANKS)[number]

export type BlokkerStatus =
  | { stage: 1; turns: number }
  | { stage: 2; turns: number }
  | { stage: 3; remaining: number }

export type Card = { suit: Suit; rank: Rank; blokker?: BlokkerStatus }

export type Grid = (Card | null)[][]
export type DealTarget = (Card | null)[]

export interface GameState {
  seed: number
  grid: Grid
  dealTarget: DealTarget
  nextHand: Card[]
  cardsRemaining: number
  points: number
  pointsSinceLastBonusCard: number
}

function mulberry32(seed: number): () => number {
  let s = seed | 0
  return () => {
    s = (s + 0x6d2b79f5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function createRng(seed: number) {
  return mulberry32(seed)
}

export class CardShoe {
  private deck: Card[] = []
  private rng: () => number

  constructor(rng: () => number) {
    this.rng = rng
  }

  draw(): Card {
    if (this.deck.length === 0) {
      this.deck = this.freshDeck()
      this.shuffle()
    }
    return this.deck.pop()!
  }

  drawN(n: number): Card[] {
    return Array.from({ length: n }, () => this.draw())
  }

  private freshDeck(): Card[] {
    const cards: Card[] = []
    for (const suit of SUITS) {
      for (const rank of RANKS) {
        cards.push({ suit, rank })
      }
    }
    return cards
  }

  private shuffle() {
    for (let i = this.deck.length - 1; i > 0; i--) {
      const j = Math.floor(this.rng() * (i + 1));
      [this.deck[i], this.deck[j]] = [this.deck[j], this.deck[i]]
    }
  }
}

export function initGame(seed: number = Date.now()): { state: GameState; rng: () => number; shoe: CardShoe; blokkerRng: () => number } {
  const rng = createRng(seed)
  const shoe = new CardShoe(rng)
  const blokkerRng = createRng(seed ^ 0x9e3779b9)
  const nextHand = shoe.drawN(5)
  const grid: Grid = Array.from({ length: 5 }, () => Array(5).fill(null))
  const dealTarget: DealTarget = Array(5).fill(null)
  return {
    state: {
      seed, grid, dealTarget, nextHand, cardsRemaining: 99,
      points: 0, pointsSinceLastBonusCard: 0,
    },
    rng,
    shoe,
    blokkerRng,
  }
}

export function canDeal(state: GameState): boolean {
  return state.nextHand.length > 0 && state.dealTarget.every(c => c === null)
}

export function isEndGame(state: GameState): boolean {
  return state.cardsRemaining === 0
    && state.nextHand.length === 0
    && state.dealTarget.every(c => c === null)
}
